import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  Button,
  useTheme,
  MenuItem,
  Select,
  FormHelperText,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { juridicaSchema } from "./validaciones";
import useMediaQuery from "@mui/material/useMediaQuery";
import { tokens } from "../../theme";

/* Iconos */
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PinDropIcon from "@mui/icons-material/PinDrop";
import HomeIcon from "@mui/icons-material/Home";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import EmailIcon from "@mui/icons-material/Email";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const apiURL = process.env.REACT_APP_API_URLDB;

const RepresentacionJuridica = ({ data, onNext, onDataChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const mainBlue = "#273c7d";

  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [municipiosFiltered, setMunicipiosFiltered] = useState([]);

  const handleFormSubmit = (values) => {
    onDataChange(values);
    onNext();
  };

  const fetchDepartamentos = async () => {
    try {
      const response = await axios.get(`${apiURL}/departamentos`);
      setDepartamentos(response.data);
    } catch (error) {
      console.error("Error al obtener departamentos", error);
    }
  };

  const fetchMunicipios = async () => {
    try {
      const response = await axios.get(`${apiURL}/municipios`);
      setMunicipios(response.data);
    } catch (error) {
      console.error("Error al obtener municipios", error);
    }
  };

  const handleDepartamentoChange = (event, setFieldValue) => {
    const departamentoID = event.target.value;
    const municipiosDelDepto = municipios.filter(
      (municipio) => municipio.IDDept === departamentoID
    );
    setMunicipiosFiltered(municipiosDelDepto);
    setFieldValue("municipio", "");
    setFieldValue("depto", departamentoID);
    onDataChange({ depto: departamentoID, municipio: "" });
  };

  useEffect(() => {
    fetchDepartamentos();
    fetchMunicipios();
  }, []);

  const initialValues = {
    razonSocial: "",
    dni: "",
    rtn: "",
    apoderado: "",
    depto: "",
    municipio: "",
    domicilio: "",
    telefono: "",
    correo: "",
  };

  return (
    <Box>
      <Typography variant="h6" color={colors.grey[300]} mb={3} textAlign="center">
        Brinde la información legal y de contacto de la entidad.
      </Typography>

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={{ ...initialValues, ...data }}
        validationSchema={juridicaSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="25px"
              gridTemplateColumns="repeat(3, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 3" },
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                label="Nombre (Razón Social)"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.razonSocial}
                name="razonSocial"
                error={!!touched.razonSocial && !!errors.razonSocial}
                helperText={touched.razonSocial && errors.razonSocial}
                sx={{ gridColumn: "span 1" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: mainBlue }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="DNI"
                onBlur={handleBlur}
                onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) handleChange(e);
                  }}
                value={values.dni}
                name="dni"
                error={!!touched.dni && !!errors.dni}
                helperText={touched.dni && errors.dni}
                sx={{ gridColumn: "span 1" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon sx={{ color: mainBlue }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="RTN"
                onBlur={handleBlur}
                onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) handleChange(e);
                  }}
                value={values.rtn}
                name="rtn"
                error={!!touched.rtn && !!errors.rtn}
                helperText={touched.rtn && errors.rtn}
                sx={{ gridColumn: "span 1" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CreditCardIcon sx={{ color: mainBlue }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="Nombre y Apellido del Apoderado"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.apoderado}
                name="apoderado"
                error={!!touched.apoderado && !!errors.apoderado}
                helperText={touched.apoderado && errors.apoderado}
                sx={{ gridColumn: "span 3" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountBoxIcon sx={{ color: mainBlue }} />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl fullWidth variant="outlined" error={!!touched.depto && !!errors.depto}>
                <InputLabel>Departamento</InputLabel>
                <Select
                  value={values.depto || ""}
                  label="Departamento"
                  onBlur={handleBlur}
                  onChange={(event) => handleDepartamentoChange(event, setFieldValue)}
                  name="depto"
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ color: mainBlue }} />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="" disabled>Seleccione un Departamento</MenuItem>
                  {departamentos.map((dept) => (
                    <MenuItem key={dept.IDDept} value={dept.IDDept}>{dept.Nombre}</MenuItem>
                  ))}
                </Select>
                {touched.depto && errors.depto && <FormHelperText>{errors.depto}</FormHelperText>}
              </FormControl>

              <FormControl fullWidth variant="outlined" error={!!touched.municipio && !!errors.municipio}>
                <InputLabel>Municipio</InputLabel>
                <Select
                  value={values.municipio || ""}
                  label="Municipio"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  name="municipio"
                  startAdornment={
                    <InputAdornment position="start">
                      <PinDropIcon sx={{ color: mainBlue }} />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="" disabled>Seleccione un Municipio</MenuItem>
                  {municipiosFiltered.map((mun) => (
                    <MenuItem key={mun.IDMunicipio} value={mun.IDMunicipio}>{mun.Nombre}</MenuItem>
                  ))}
                </Select>
                {touched.municipio && errors.municipio && <FormHelperText>{errors.municipio}</FormHelperText>}
              </FormControl>

              <TextField
                fullWidth
                variant="outlined"
                label="Domicilio Legal"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.domicilio}
                name="domicilio"
                error={!!touched.domicilio && !!errors.domicilio}
                helperText={touched.domicilio && errors.domicilio}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HomeIcon sx={{ color: mainBlue }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="Teléfono"
                onBlur={handleBlur}
                onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) handleChange(e);
                  }}
                value={values.telefono}
                name="telefono"
                error={!!touched.telefono && !!errors.telefono}
                helperText={touched.telefono && errors.telefono}
                sx={{ gridColumn: "span 1" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocalPhoneIcon sx={{ color: mainBlue }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="Correo Electrónico"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.correo}
                name="correo"
                error={!!touched.correo && !!errors.correo}
                helperText={touched.correo && errors.correo}
                sx={{ gridColumn: "span 2" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: mainBlue }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box display="flex" justifyContent="center" mt="40px">
              <Button
                type="submit"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  backgroundColor: mainBlue,
                  color: "white",
                  px: 6,
                  py: 1.2,
                  borderRadius: "10px",
                  fontWeight: "bold",
                  textTransform: "none",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#1c2a4a",
                    transform: "translateX(5px)",
                    boxShadow: "0px 8px 15px rgba(0,0,0,0.2)",
                  },
                }}
              >
                Siguiente Paso
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default RepresentacionJuridica;
